import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, distinctUntilChanged, forkJoin, map, Observable, of, retry, tap } from "rxjs";
import { ENVIRONMENT } from "../../../const";
import { HttpClient } from "@angular/common/http";
import { Insegnamento, SubtenantsResponse } from "../models/insegnamento.model";
import { AppConfig } from "../models/settings";
import { SubtenantService } from "./subtenant.service";

export interface InsegnamentoItem {
    id: string | number;
    idDocente: string | number;
    corso: string;
    tipoCorso: string;
    classeLaurea: string;
    insegnamento: string;
    indirizzo: string;
    dipartimento: string;
    docente: string;
    sede: string;
    cds: string;
    coorte: string;
}

export interface InsegnamentoId {
    id: string | number;
}

export interface InsegnamentoConfig {
    id?: string | number;
    placeholder?: string;
    defaultSelected?: string | number;
    title?: string;
    maxItemsVisibility?: number;
    visible?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class InsegnamentoService {

    private readonly configSubject = new BehaviorSubject<InsegnamentoConfig | null>(null);
    private readonly teachingsSubject = new BehaviorSubject<InsegnamentoItem[]>([]);
    private readonly teachingConfigSubject = new BehaviorSubject<InsegnamentoConfig>({});
    private readonly selectedTeachingSubject = new BehaviorSubject<InsegnamentoItem | null>(null);
    private readonly loadedSubject = new BehaviorSubject<boolean>(false);
    private readonly loadErrorSubject = new BehaviorSubject<boolean>(false);
    private readonly endpoint: string;

    teachings$ = this.teachingsSubject.asObservable();
    teachingConfig$ = this.teachingConfigSubject.asObservable();
    selectedTeaching$ = this.selectedTeachingSubject.asObservable().pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id)
    );
    isLoaded$ = this.loadedSubject.asObservable();
    loadError$ = this.loadErrorSubject.asObservable();

    constructor(
      @Inject(ENVIRONMENT) protected environment: any,
      private http: HttpClient,
      private subtenantService: SubtenantService
    ) {
        this.endpoint = environment.endpoint;
        this.loadData();
    }

    loadData() {
        this.loadErrorSubject.next(false);
        forkJoin({
            teachings: this.getTeachings().pipe(retry({ count: 2, delay: 800 })),
            teachingConfig: this.getTeachingConfig()
        }).subscribe({
            next: ({ teachings, teachingConfig }) => {
                this.loadErrorSubject.next(false);
                const teachingItems = teachings.map(teaching => this.toInsegnamentoItem(teaching));

                this.teachingsSubject.next(teachingItems);

                let itemToSelect: InsegnamentoItem | null = null;
                let configUpdate: InsegnamentoConfig = teachingConfig;

                const restoredTeachingId = sessionStorage.getItem('selectedTeachingId');
                if (restoredTeachingId) {
                    itemToSelect = teachingItems.find(item => String(item.id) === restoredTeachingId) || null;
                    if (!itemToSelect) {
                        sessionStorage.removeItem('selectedTeachingId');
                    }
                }

                if (!itemToSelect && teachingItems.length === 1) {
                    itemToSelect = teachingItems[0];
                    sessionStorage.setItem('selectedTeachingId', String(itemToSelect.id));
                    console.log('Selezione automatica del singolo insegnamento.');
                }

                this.selectedTeachingSubject.next(itemToSelect);

                if (itemToSelect) {
                    configUpdate = {
                        ...teachingConfig,
                        title: itemToSelect.insegnamento,
                        id: itemToSelect.id
                    };
                    console.log(`Insegnamento finale: ${itemToSelect.insegnamento}`);
                } else {
                    configUpdate = {
                        ...teachingConfig,
                        title: teachingConfig.placeholder ?? '',
                        id: undefined
                    };
                    console.log('Nessun insegnamento selezionato o ripristinato.');
                }

                this.teachingConfigSubject.next(configUpdate);

                if (itemToSelect && !this.subtenantService.getSubtenantToken()) {
                    this.subtenantService.selectSubtenant(itemToSelect.id).subscribe({
                        next: () => this.loadedSubject.next(true),
                        error: (err) => {
                            console.error('InsegnamentoService: Failed to select subtenant', err);
                            this.loadedSubject.next(true);
                        }
                    });
                } else {
                    this.loadedSubject.next(true);
                }
            },
            error: (err) => {
                console.error('InsegnamentoService: Failed to load teachings data', err);
                this.loadErrorSubject.next(true);
                this.loadedSubject.next(true);
            }
        });
    }

    reload(): void {
        this.loadedSubject.next(false);
        this.loadData();
    }

    private toInsegnamentoItem(teaching: Insegnamento): InsegnamentoItem {
        return {
            id: teaching.id,
            idDocente: teaching.userId,
            corso: teaching.courseId,
            tipoCorso: teaching.course.name,
            classeLaurea: teaching.course.degreeClass,
            insegnamento: teaching.title,
            indirizzo: teaching.address,
            dipartimento: teaching.departmentName,
            docente: [teaching.user.name, teaching.user.surname].filter(Boolean).join(' '),
            sede: teaching.locationName,
            cds: teaching.course.degreeClass.replace('-', ' '),
            coorte: teaching.year,
        };
    }

    reloadTeachings(): Observable<InsegnamentoItem[]> {
        return this.getTeachings().pipe(
            map(teachings => teachings.map(teaching => this.toInsegnamentoItem(teaching))),
            tap(items => this.teachingsSubject.next(items))
        );
    }

    getTeachings(): Observable<Insegnamento[]> {
        return this.http.get<SubtenantsResponse[]>(`${this.endpoint}/user/subtenants`).pipe(map((response: SubtenantsResponse[]) =>
        response.map(item => this.toInsegnamento(item))
      ));
    }

    private toInsegnamento(data: SubtenantsResponse): Insegnamento {
        const attrs = data.customAttributes ?? {};
        return {
            id: data.id,
            title: attrs['title'] ?? data.name,
            userId: attrs['userId'] ?? '',
            courseId: attrs['courseId'] ?? '',
            departmentName: attrs['departmentName'] ?? '',
            address: attrs['address'] ?? '',
            locationName: attrs['locationName'] ?? '',
            year: attrs['year'] ?? '',
            course: {
                id: attrs['courseId'] ?? '',
                name: data.name,
                degreeClass: attrs['degreeClass'] ?? ''
            },
            user: {
                id: attrs['userId'] ?? '',
                name: ''
            }
        } as Insegnamento;
    }

    getTeachingConfig(): Observable<InsegnamentoConfig> {

        const data: InsegnamentoConfig = {
            id: 'teachingConfig',
            placeholder: 'Seleziona un insegnamento',
            title: '',
            maxItemsVisibility: 5,
            visible: true
        };

        return new Observable<InsegnamentoConfig>(subscriber => {
            subscriber.next(data);
            subscriber.complete();
        });
    }

    // getTeachingConfig(): Observable<InsegnamentoConfig> {
    //     return this.http.get<AppConfig>(`${this.endpoint}/user/config/teaching-config`)
    //       .pipe(
    //         map(appConfig => {
    //             try {
    //                 return JSON.parse(appConfig.value) as InsegnamentoConfig;
    //             } catch (e) {
    //                 console.error("Failed to parse appConfig for teachings", e);
    //                 return {};
    //             }
    //         })
    //       );
    // }

    clearSubmenu(): void {
        this.selectedTeachingSubject.next(null);
        sessionStorage.removeItem('selectedTeachingId');
        this.subtenantService.clearSubtenantToken();
    }

    selectInsegnamento(value: string | number): Observable<boolean> {
        return this.subtenantService.selectSubtenant(value).pipe(
            map(() => this.updateSelection(value)),
            catchError((err) => {
                console.error('InsegnamentoService: Failed to select subtenant', err);
                return of(false);
            })
        );
    }

    updateSelection(value: string | number): boolean {
        const selectedItem = this.teachingsSubject.value.find(it => String(it.id) === String(value));
        if (!selectedItem) return false;
        this.selectedTeachingSubject.next(selectedItem);
        sessionStorage.setItem('selectedTeachingId', String(selectedItem.id));
        console.log(`Insegnamento salvato: ${selectedItem.insegnamento}`);
        this.teachingConfigSubject.next({
            ...this.teachingConfigSubject.value,
            title: selectedItem.insegnamento,
            id: selectedItem.id
        });
        return true;
    }

    getCurrentSelection(): string | number | undefined {
        return this.selectedTeachingSubject.value?.id;
    }

    getCurrentConfig(): InsegnamentoConfig | null {
        return this.teachingConfigSubject.value;
    }

    getItemById(id: string | number): InsegnamentoItem | null {
        return this.teachingsSubject.value.find(item => item.id === id) || null;
    }

}